<template>
	<button
		:href="href"
		class="color"
		:class="{ active }"
		:disabled="disabled"
		@focusin="$emit('set-focused', color)"
		@focusout="$emit('remove-focused', color)"
		@mouseenter="$emit('set-hovered', color)"
		@mouseleave="$emit('remove-hovered', color)"
	>
		<div
			class="outer-circle"
			v-if="size === 'md' || size === 'lg'"
		>
			<div
				class="inner-circle"
				:style="{ backgroundColor: color }"
			>
				<svg
					v-if="disabled"
					class="rectangle"
					width="50"
					height="5"
				>
					<rect
						width="36"
						height="3"
						style="fill: rgb(255 255 255)"
					/>
				</svg>
			</div>
		</div>
		<div
			class="inner-circle"
			:style="{ backgroundColor: color }"
			v-else
		>
			<svg
				v-if="disabled"
				class="rectangle"
				width="50"
				height="5"
			>
				<rect
					width="36"
					height="3"
					style="fill: rgb(255 255 255)"
				/>
			</svg>
		</div>
		<div
			class="label"
			:class="{ 'label-visible': showLabel }"
			v-if="label"
		>
			{{ label }}
		</div>
	</button>
</template>

<script>
export default defineNuxtComponent({
	name: "Color",

	props: {
		color: {
			type: String,
			required: true,
		},
		label: {
			type: String,
		},
		showLabel: {
			type: Boolean,
		},
		size: {
			type: String,
			validator: (size) => ["sm", "md", "lg"].includes(size),
		},
		active: {
			type: Boolean,
		},
		disabled: {
			type: Boolean,
		},
		href: {
			type: String,
			default: "javascript:",
		},
	},
});
</script>

<style lang="scss" scoped>
.color {
	display: block;
	text-decoration: none;
	color: inherit;
	width: 3.5rem;
	background: none;
	padding: 0;
	border: 0;
	outline: 0;

	.outer-circle {
		width: 3.5rem;
		height: 3.5rem;
	}

	&:focus {
		outline: none;
	}

	&:hover {
		cursor: pointer;
	}

	&:disabled {
		cursor: auto;
	}

	&:hover:not(.active) {
		.outer-circle {
			outline: 1px solid $color-cta-active;
		}

		&.active {
			.outer-circle {
				outline: 2px solid $color-cta-active;
			}
		}
	}

	&:focus:not(.active, :hover) {
		.outer-circle {
			outline: 2px solid $color-cta-focus;
		}
	}

	.label {
		opacity: 0;
		transition: opacity 0.2s ease;
		text-align: center;
		margin-top: $spacing-s;
		color: $color-neutrals-600-shade;
		white-space: nowrap;
		display: flex;
		justify-content: center;

		@include font-text-xs;
	}

	.label-visible {
		opacity: 1;
	}

	&:first-child {
		.label {
			text-align: left;
			padding-left: 0.43rem;
			justify-content: start;
		}
	}

	&:nth-child(n + 2):last-child {
		.label {
			text-align: right;
			padding-right: 0.43rem;
			justify-content: end;
		}
	}

	&.active {
		.outer-circle {
			outline: 2px solid $color-cta-active;
		}
	}
}

.outer-circle,
.inner-circle {
	width: 100%;
	height: 100%;
}

.outer-circle {
	border-radius: 50%;
	padding: 0.43rem;
}

.inner-circle {
	border-radius: 50%;
	position: relative;
	overflow: hidden;
	box-shadow: 0 3px 4px 0 #0003 inset;

	.rectangle {
		position: absolute;
		transform: rotate(45deg);
		z-index: 100;
		top: 50%;
		left: -0.625rem;
	}

	&::after {
		content: "";
		position: absolute;
		inset: 0;
	}
}
</style>
